import { useEffect, useRef, useState } from "react";
import ReactCrop, { centerCrop, Crop, makeAspectCrop, PixelCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";

function centerAspectCrop(mediaWidth: number, mediaHeight: number, aspect: number) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: "%",
        width: 50,
      },
      aspect,
      mediaWidth,
      mediaHeight
    ),
    mediaWidth,
    mediaHeight
  );
}

export interface CropImageProps {
  imgSrc: string | ArrayBuffer | null;
  onChange: (image: Blob | null) => void;
}

export function CropImage(props: CropImageProps) {
  const { imgSrc, onChange } = props;
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const imgRef = useRef<HTMLImageElement | null>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const aspect = 1;

  /**
   * Save the reference of the original image when is loaded in ReactCrop
   *
   * @param e
   */
  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    if (aspect) {
      const { width, height } = e.currentTarget;
      const initialCrop = centerAspectCrop(width, height, aspect);
      setCrop(initialCrop);
      // Set the completed crop immediately so the initial crop is processed
      const pixelCrop: PixelCrop = {
        unit: "px",
        x: (initialCrop.x / 100) * width,
        y: (initialCrop.y / 100) * height,
        width: (initialCrop.width / 100) * width,
        height: (initialCrop.height / 100) * height,
      };
      setCompletedCrop(pixelCrop);
    }
  };

  /**
   * Draw the image in a canvas to be able to save it later as Blob
   */
  useEffect(() => {
    if (completedCrop && previewCanvasRef.current && imgRef.current) {
      const image = imgRef.current;
      const canvas = previewCanvasRef.current;
      const clonedCompletedCrop = structuredClone(completedCrop);

      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;
      const ctx = canvas.getContext("2d");
      const pixelRatio = window.devicePixelRatio;

      canvas.width = clonedCompletedCrop.width * pixelRatio * scaleX;
      canvas.height = clonedCompletedCrop.height * pixelRatio * scaleY;

      if (ctx) {
        ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
        ctx.imageSmoothingQuality = "high";

        ctx.drawImage(
          image,
          clonedCompletedCrop.x * scaleX,
          clonedCompletedCrop.y * scaleY,
          clonedCompletedCrop.width * scaleX,
          clonedCompletedCrop.height * scaleY,
          0,
          0,
          clonedCompletedCrop.width * scaleX,
          clonedCompletedCrop.height * scaleY
        );

        // Call toBlob after the image has been drawn to the canvas
        canvas.toBlob((blob) => {
          onChange(blob);
        }, "image/jpeg");
      }
    }
  }, [completedCrop, onChange]);

  return (
    <>
      <div className="flex justify-center">
        {typeof imgSrc === "string" ? (
          <ReactCrop
            aspect={1}
            circularCrop
            crop={crop}
            keepSelection
            minHeight={96}
            minWidth={96}
            onChange={(c) => setCrop(c)}
            onComplete={(c) => setCompletedCrop(c)}
          >
            <img alt="to crop" onLoad={onImageLoad} ref={imgRef} src={imgSrc} style={{ maxHeight: "75dvh" }} />
          </ReactCrop>
        ) : null}
      </div>
      <canvas
        hidden
        ref={previewCanvasRef}
        style={{
          width: Math.round(completedCrop?.width ?? 0),
          height: Math.round(completedCrop?.height ?? 0),
        }}
      />
    </>
  );
}

export default CropImage;
